/**
 * Sentry Configuration - Lightweight Console-Based Implementation
 *
 * Provides real error tracking, breadcrumb buffering, user context,
 * and performance transactions using browser APIs.
 * A full Sentry SDK can be enabled via plugins for production use.
 */

// Environment configuration
const ENVIRONMENT = import.meta.env.VITE_ENV || 'development';

// ── Module-level state ────────────────────────────────────

let _currentUser: { id?: string; email?: string; username?: string } | null = null;
const _contexts: Map<string, Record<string, unknown>> = new Map();

interface Breadcrumb {
  timestamp: number;
  category?: string;
  message?: string;
  level?: 'debug' | 'info' | 'warning' | 'error';
  data?: Record<string, unknown>;
}

const MAX_BREADCRUMBS = 100;
const _breadcrumbs: Breadcrumb[] = [];

let _initialized = false;

// ── Helpers ───────────────────────────────────────────────

function _enrichContext(): Record<string, unknown> {
  const enriched: Record<string, unknown> = {};
  if (_currentUser) enriched.user = { ..._currentUser };
  if (_contexts.size > 0) {
    for (const [name, ctx] of _contexts.entries()) {
      enriched[name] = ctx;
    }
  }
  if (_breadcrumbs.length > 0) {
    enriched.breadcrumbs = _breadcrumbs.slice(-10); // attach last 10
  }
  return enriched;
}

// ── Public API ────────────────────────────────────────────

/**
 * Initialize error tracking.
 * Sets up a global error handler via `window.addEventListener('error', ...)`.
 */
export function initSentry(): void {
  if (_initialized) return;
  _initialized = true;

  if (typeof window !== 'undefined') {
    window.addEventListener('error', (event) => {
      captureException(
        event.error ?? new Error(event.message),
        { source: event.filename, lineno: event.lineno, colno: event.colno },
      );
    });

    window.addEventListener('unhandledrejection', (event) => {
      const error =
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason));
      captureException(error, { type: 'unhandledrejection' });
    });
  }

  console.log('[Sentry] Lightweight error tracking initialized', { environment: ENVIRONMENT });
}

/**
 * Capture an exception with stack trace and enriched context.
 */
export function captureException(error: Error, context?: Record<string, unknown>): void {
  const enriched = { ..._enrichContext(), ...context };
  console.error('[Sentry:Exception]', error.message, '\n  Stack:', error.stack, '\n  Context:', enriched);

  addBreadcrumb({
    category: 'error',
    message: error.message,
    level: 'error',
    data: context,
  });
}

/**
 * Capture an error (alias for captureException).
 */
export function captureError(error: Error, context?: Record<string, unknown>): void {
  captureException(error, context);
}

/**
 * Capture a message with level-appropriate logging.
 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info',
  context?: Record<string, unknown>,
): void {
  const enriched = { ..._enrichContext(), ...context };
  const logFn = level === 'error' ? console.error : level === 'warning' ? console.warn : console.info;
  if (Object.keys(enriched).length > 0) {
    logFn(`[Sentry:${level.toUpperCase()}]`, message, enriched);
  } else {
    logFn(`[Sentry:${level.toUpperCase()}]`, message);
  }

  addBreadcrumb({ category: 'message', message, level: level === 'warning' ? 'warning' : level === 'error' ? 'error' : 'info' });
}

/**
 * Set user context for error enrichment.
 */
export function setUser(user: { id?: string; email?: string; username?: string } | null): void {
  _currentUser = user;
  if (user) {
    console.debug('[Sentry] User context set:', user.id ?? user.email ?? user.username);
  } else {
    console.debug('[Sentry] User context cleared');
  }
}

/**
 * Set custom context data for enriching future captures.
 */
export function setContext(name: string, context: Record<string, unknown>): void {
  _contexts.set(name, context);
}

/**
 * Add a breadcrumb to the rotating buffer (max 100).
 * Supports both object and positional argument styles.
 */
export function addBreadcrumb(
  messageOrBreadcrumb: string | {
    category?: string;
    message?: string;
    level?: 'debug' | 'info' | 'warning' | 'error';
    data?: Record<string, unknown>;
  },
  category?: string,
  data?: Record<string, unknown>,
): void {
  let crumb: Breadcrumb;
  if (typeof messageOrBreadcrumb === 'string') {
    crumb = { timestamp: Date.now(), message: messageOrBreadcrumb, category, data };
  } else {
    crumb = { timestamp: Date.now(), ...messageOrBreadcrumb };
  }

  _breadcrumbs.push(crumb);
  if (_breadcrumbs.length > MAX_BREADCRUMBS) {
    _breadcrumbs.splice(0, _breadcrumbs.length - MAX_BREADCRUMBS);
  }
}

/**
 * Retrieve the stored breadcrumb trail for debugging.
 * Returns a shallow copy so callers cannot mutate the internal buffer.
 */
export function getBreadcrumbs(): Breadcrumb[] {
  return _breadcrumbs.slice();
}

/**
 * Span-like object returned by startTransaction.
 * Provides the full surface expected by callers so performance data is never
 * silently discarded.
 */
export interface Span {
  finish: () => void;
  setStatus: (status: string) => void;
  setTag: (key: string, value: string) => void;
  setData: (key: string, value: unknown) => void;
  startChild: (ctx: { op?: string; description?: string }) => Span;
}

/**
 * Start a transaction for performance monitoring.
 * Uses `performance.mark()` / `performance.measure()` for real timing.
 * The returned Span exposes `setTag`, `setData`, and `startChild` so
 * callers can annotate spans without crashes or silent data loss.
 */
export function startTransaction(context: {
  name: string;
  op?: string;
  description?: string;
}): Span {
  const markName = `sentry-txn:${context.name}`;
  let status = 'ok';
  const tags: Record<string, string> = {};
  const data: Record<string, unknown> = {};
  const children: Span[] = [];

  if (typeof performance !== 'undefined') {
    performance.mark(`${markName}-start`);
  }
  const startTime = Date.now();

  const span: Span = {
    setStatus(s: string) {
      status = s;
    },
    setTag(key: string, value: string) {
      tags[key] = value;
    },
    setData(key: string, value: unknown) {
      data[key] = value;
    },
    startChild(childCtx: { op?: string; description?: string }): Span {
      const child = startTransaction({
        name: `${context.name} > ${childCtx.description ?? childCtx.op ?? 'child'}`,
        op: childCtx.op,
        description: childCtx.description,
      });
      children.push(child);
      return child;
    },
    finish() {
      // Finish any open children first
      for (const child of children) {
        child.finish();
      }

      let durationMs = Date.now() - startTime;
      if (typeof performance !== 'undefined') {
        try {
          performance.mark(`${markName}-end`);
          const measure = performance.measure(markName, `${markName}-start`, `${markName}-end`);
          durationMs = measure.duration;
        } catch {
          // marks may have been cleared
        }
      }
      console.debug(
        `[Sentry:Transaction] ${context.op ? `[${context.op}] ` : ''}${context.name} — ${durationMs.toFixed(1)}ms (${status})`,
      );
      addBreadcrumb({
        category: 'transaction',
        message: `${context.name} finished in ${durationMs.toFixed(1)}ms`,
        level: status === 'ok' ? 'info' : 'warning',
        data: { op: context.op, status, durationMs, tags, spanData: data },
      });
    },
  };

  return span;
}

/**
 * Create a temporary context scope and run the callback within it.
 */
export function withSentryScope<T>(
  callback: (scope: { setTag: (key: string, value: string) => void }) => T,
): T {
  const scopeTags: Record<string, string> = {};
  const scope = {
    setTag: (key: string, value: string) => {
      scopeTags[key] = value;
    },
  };

  // Temporarily add scope tags as context
  setContext('_scope_tags', scopeTags);
  try {
    return callback(scope);
  } finally {
    _contexts.delete('_scope_tags');
  }
}
